import {
  ALL_ACCOUNTS,
  isCancellable,
  isWorkingOrder,
  notActionableReason,
  pendingLabel,
  type AccountScope,
  type OrderEvent,
} from "@helm/core";
import { flattenLabel } from "@helm/ui";
import { TradeButton } from "../components/TradeButton";
import { useSelectionSlice, type PositionRow } from "../viewmodel/account";
import { useBusy, useGate } from "./control";
import { cancelOrder, exitAction, requestFlattenAll } from "./actions";

/**
 * Row and toolbar actions for the account tables (Tradovate names; SPEC §9.7 semantics), each
 * gated like every other trading control (mock / Rithmic Test only, READY, a known account).
 * Row actions act on the row's own account, so they work while the top bar shows All accounts.
 */

/**
 * Orders row: cancel one working order. An order waiting on Rithmic (a cancel already in flight,
 * not yet acknowledged, or UNKNOWN) shows as pending instead: the gateway refuses a cancel in those
 * states. An order whose modify is unanswered shows "Modifying" and can still be cancelled (the
 * cancel supersedes the modify, ADR-0032).
 */
export function OrderCancelButton({ order }: { order: OrderEvent }): React.JSX.Element | null {
  const key = `row:cxl:${order.clientOrderId}`;
  const gate = useGate({ kind: "cancel", accountId: order.accountId });
  const busy = useBusy(key);
  if (!isWorkingOrder(order)) return null;
  const pending = pendingLabel(order);
  if (pending !== null && !isCancellable(order)) {
    const why = notActionableReason([order]) ?? pending;
    return (
      <span className="muted order-pending" data-order-pending={pending} title={why} aria-label={why}>
        {pending}…
      </span>
    );
  }
  return (
    <TradeButton
      size="sm"
      semantics={`Cancel order ${order.clientOrderId}`}
      label={`Cancel ${order.symbol} order ${order.clientOrderId}`}
      gate={gate}
      busy={busy}
      onPress={() => {
        void cancelOrder(key, order.accountId, order.clientOrderId, `Cancel ${order.symbol} order`);
      }}
    >
      {pending !== null ? `${pending}… Cancel` : "Cancel"}
    </TradeButton>
  );
}

/**
 * Positions row: "Exit at Mkt & Cxl" (Tradovate) — cancels the symbol's working orders, then exits
 * the position at market. Short "Close" label for narrow tables; the full meaning is in the
 * tooltip and the accessible name.
 */
export function PositionCloseButton({
  p,
  account,
}: {
  p: Pick<PositionRow, "accountId" | "symbol" | "netQty">;
  account: string;
}): React.JSX.Element {
  const key = `row:exit:${p.accountId}:${p.symbol}`;
  const gate = useGate({ kind: "exitSymbol", accountId: p.accountId, contract: p.symbol });
  const busy = useBusy(key);
  const long = p.netQty > 0;
  return (
    <TradeButton
      size="sm"
      // §9.7: closing a long sells (sell colour), closing a short buys (buy colour).
      tone={long ? "sell" : "buy"}
      semantics={`Exit at Mkt & Cxl: cancels ${p.symbol} working orders on ${account}, then ${long ? "sells" : "buys"} ${String(Math.abs(p.netQty))} at market`}
      label={`Exit at Mkt & Cxl ${p.symbol} ${account}`}
      gate={gate}
      busy={busy}
      onPress={() => {
        void exitAction(
          key,
          "EXIT_AND_CANCEL_SYMBOL",
          p.accountId,
          p.symbol,
          `Exit at Mkt & Cxl ${p.symbol} · ${account}`,
        );
      }}
    >
      Close
    </TradeButton>
  );
}

/** Accounts row: flatten that account (cancel all, then exit every position; no confirm). */
export function AccountFlattenButton({
  accountId,
  label,
}: {
  accountId: string;
  label: string;
}): React.JSX.Element {
  const key = `row:flatten:${accountId}`;
  const gate = useGate({ kind: "flattenAccount", accountId });
  const busy = useBusy(key);
  return (
    <TradeButton
      size="sm"
      tone="flatten"
      semantics={`Exit All Positions & Cxl All Orders on ${label}`}
      label={`Flatten account ${label}`}
      gate={gate}
      busy={busy}
      onPress={() => {
        void exitAction(
          key,
          "FLATTEN_ACCOUNT",
          accountId,
          null,
          `Exit All Positions & Cxl All Orders · ${label}`,
        );
      }}
    >
      Flatten
    </TradeButton>
  );
}

/**
 * Exit All Positions & Cxl All Orders for a module's account scope (Tradovate semantics; visible
 * label names the scope like the order surfaces, owner 2026-10-04): one account → "Flatten acct",
 * that account's flatten (no confirm); all accounts → "Flatten all", Exit All for every account,
 * which asks first when "Ask before Exit All" is on. The accessible names keep the full meaning;
 * the command still uses the same explicit exit intent.
 */
export function ExitAllButton({
  scope,
  label,
}: {
  scope: AccountScope;
  label?: string | undefined;
}): React.JSX.Element {
  const all = scope.kind === "all";
  const accountId = scope.kind === "one" ? scope.id : null;
  const key = accountId === null ? "flatten-all" : `exit-all:${accountId}`;
  const allGate = useGate({ kind: "flattenAll" });
  const oneGate = useGate({ kind: "flattenAccount", accountId });
  const busy = useBusy(key);
  const name = label ?? accountId ?? "";
  return (
    <TradeButton
      size="sm"
      tone="flatten"
      semantics={
        all
          ? "Exit All — all accounts: cancels every working order and closes every position on every account; asks first when 'Ask before Exit All' is on (Settings)"
          : `Exit All Positions & Cxl All Orders on ${name}`
      }
      label={all ? "Exit All — all accounts" : `Exit All Positions & Cxl All Orders ${name}`}
      gate={all ? allGate : oneGate}
      busy={busy}
      onPress={() => {
        if (all) requestFlattenAll();
        else
          void exitAction(
            key,
            "FLATTEN_ACCOUNT",
            accountId,
            null,
            `Exit All Positions & Cxl All Orders · ${name}`,
          );
      }}
    >
      {flattenLabel(all)}
    </TradeButton>
  );
}

/** Flatten ALL accounts, following the separate confirmation preference. */
export function FlattenAllButton({
  children = "Flatten All",
}: {
  children?: React.ReactNode;
}): React.JSX.Element {
  const gate = useGate({ kind: "flattenAll" });
  const busy = useBusy("flatten-all");
  return (
    <TradeButton
      size="sm"
      tone="flatten"
      semantics="Exit All — all accounts: flattens every connected account; asks first when 'Ask before Exit All' is on (Settings)"
      hotkey="Ctrl+Shift+F"
      gate={gate}
      busy={busy}
      onPress={requestFlattenAll}
    >
      {children}
    </TradeButton>
  );
}

/**
 * Top-bar "Flatten acct" (owner 2026-09-27): the selected account's flatten, or — while All
 * accounts is selected — "Flatten all", Exit All for every account, with the same Exit All
 * confirmation preference. Danger-exit solid, never the Sell colour (owner 2026-10-04).
 */
export function TopBarFlatten(): React.JSX.Element {
  const tv = useSelectionSlice();
  const all = tv.selection === ALL_ACCOUNTS;
  const accountId = all ? null : tv.selection;
  const key = accountId === null ? "flatten-all" : `topbar:flatten:${accountId}`;
  const allGate = useGate({ kind: "flattenAll" });
  const oneGate = useGate({ kind: "flattenAccount", accountId });
  const busy = useBusy(key);
  const name = tv.accounts.find((a) => a.summary.id === accountId)?.summary.label ?? accountId ?? "";
  return (
    <TradeButton
      size="sm"
      tone="flatten"
      className="topbar-flatten"
      semantics={
        all
          ? "Flatten every account: cancels all orders and closes all positions on every account; asks first when 'Ask before Exit All' is on (Settings)"
          : `Flatten ${name}: cancels all its orders and closes all its positions at market`
      }
      label={all ? "Exit all accounts" : `Exit all ${name}`}
      gate={all ? allGate : oneGate}
      busy={busy}
      onPress={() => {
        if (all) requestFlattenAll();
        else void exitAction(key, "FLATTEN_ACCOUNT", accountId, null, `Flatten · ${name}`);
      }}
    >
      {flattenLabel(all)}
    </TradeButton>
  );
}
